import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Copy, MessageCircle, Check, Share2, PlusCircle, X } from "lucide-react";
import { toast } from "sonner";

export default function ReferralCard({ onCommitted, pendingCount = 0, maxPending = 5 }) {
  const [preview, setPreview] = useState(null);        // { code, share_text, wa_share_url, ... }
  const [committed, setCommitted] = useState(false);   // true once the entry has been persisted
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const atLimit = pendingCount >= maxPending;

  const startPreview = async () => {
    if (atLimit) return;
    setBusy(true);
    try {
      const { data } = await api.post("/referrals/preview");
      setPreview(data);
      setCommitted(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const discardPreview = () => {
    setPreview(null);
    setCommitted(false);
  };

  const commitAndOpen = async (url) => {
    if (!preview) return;
    if (committed) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    setCommitting(true);
    try {
      const { data } = await api.post("/referrals/confirm", { code: preview.code });
      setPreview(data);
      setCommitted(true);
      toast.success(`Referral ${data.code} sent and logged`);
      onCommitted?.();
      // Open WhatsApp in a new tab AFTER the entry has been logged
      const finalUrl = url.includes(preview.code) ? url : data.wa_share_url;
      window.open(finalUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log referral");
    } finally { setCommitting(false); }
  };

  const copy = async () => {
    if (!preview) return;
    await navigator.clipboard.writeText(preview.code);
    setCopied(true);
    toast.success("Referral code copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const copyShare = async () => {
    if (!preview) return;
    await navigator.clipboard.writeText(preview.share_text);
    toast.success("Share message copied");
  };

  // ==== EMPTY STATE (no active preview) ====
  if (!preview) {
    return (
      <div className="velo-card p-5 sm:p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-pink-500/10 blur-3xl" />
        <div className="relative">
          <div className="text-xs uppercase tracking-[0.2em] text-pink-400 mb-3">Start Referring</div>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-light text-white leading-tight max-w-md">
            One tap to generate a fresh referral code.
          </h2>
          <p className="mt-3 sm:mt-4 text-sm text-slate-400 leading-relaxed max-w-lg">
            No forms. Click New Referral, we'll show a unique code and a ready-to-send WhatsApp message. Nothing is logged until you actually tap Share on WhatsApp — then it lands in the admin queue as Pending.
          </p>
          {atLimit ? (
            <div data-testid="pending-limit-banner" className="mt-6 p-4 rounded-lg border border-amber-500/30 bg-amber-500/10 max-w-lg">
              <div className="text-sm text-amber-200">You have reached your maximum referral limit. Please close an existing referral before creating a new one.</div>
            </div>
          ) : null}
          <Button data-testid="btn-new-referral" onClick={startPreview} disabled={busy || atLimit} className="mt-6 h-13 px-8 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-semibold text-base transition-all hover:scale-[1.02] active:scale-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100">
            <PlusCircle className="w-5 h-5 mr-2" strokeWidth={2} /> {busy ? "Generating…" : "New Referral"}
          </Button>
          <div className="mt-3 text-xs text-slate-500">Pending: <span className="font-mono text-slate-300">{pendingCount}</span> / {maxPending}</div>
        </div>
      </div>
    );
  }

  // ==== PREVIEW / COMMITTED STATE ====
  return (
    <div className="velo-card p-5 sm:p-8 relative overflow-hidden">
      <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-pink-500/5 blur-3xl" />
      <div className="relative">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-pink-400">
              {committed ? "Referral Sent" : "Ready to Share"}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">Pending {pendingCount} / {maxPending}</div>
          </div>
          <Button data-testid="btn-discard-preview" onClick={discardPreview} size="sm" variant="ghost" className="rounded-full text-slate-400 hover:text-white hover:bg-white/5 h-9">
            <X className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> {committed ? "Done" : "Discard"}
          </Button>
        </div>

        <div className="flex items-center gap-3 flex-wrap mt-4">
          <div data-testid="referral-code-text" className="font-mono text-2xl sm:text-3xl lg:text-4xl text-white font-medium tracking-wider break-all">{preview.code}</div>
          <Button data-testid="btn-copy-code" onClick={copy} variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5 h-10">
            {copied ? <Check className="w-4 h-4 mr-2 text-emerald-400" /> : <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} />} {copied ? "Copied" : "Copy"}
          </Button>
        </div>

        {committed ? (
          <div data-testid="committed-banner" className="mt-4 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10">
            <div className="text-xs text-emerald-200">Logged as <span className="font-mono">Pending</span> — Veloskin admin will close it once your friend books.</div>
          </div>
        ) : (
          <p className="mt-4 text-sm text-slate-400 leading-relaxed">
            Nothing is logged yet. Tap Share on WhatsApp to send the message and register this code as a pending referral.
          </p>
        )}

        <div className="mt-6 p-5 velo-surface rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-slate-500 uppercase tracking-wider">Message to send your friend</div>
            <button data-testid="btn-copy-share" onClick={copyShare} className="text-xs text-pink-400 hover:text-pink-300 flex items-center gap-1">
              <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy
            </button>
          </div>
          <div data-testid="share-message-text" className="text-sm text-slate-200 leading-relaxed whitespace-pre-line">{preview.share_text}</div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
          <Button
            data-testid="btn-share-whatsapp"
            onClick={() => commitAndOpen(preview.wa_share_url)}
            disabled={committing}
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm transition-all hover:scale-[1.02] active:scale-100 disabled:opacity-60 w-full sm:w-auto"
          >
            <Share2 className="w-4 h-4" strokeWidth={2.5} /> {committing ? "Logging…" : "Share on WhatsApp"}
          </Button>
          <Button
            data-testid="btn-msg-salesperson"
            onClick={() => commitAndOpen(preview.wa_sales_url)}
            disabled={committing}
            variant="outline"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-white/20 text-white bg-transparent hover:bg-white/5 font-medium text-sm w-full sm:w-auto"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Chat with Veloskin sales
          </Button>
        </div>
      </div>
    </div>
  );
}
